"use client";

import { IUserData } from "@/types";
import React from "react";
import { cn } from "@/lib/utils";
import ProfileBadges from "@/components/profileBadges";
import { masks } from "@/lib/imageMasks";
import { IconLocation, IconUser } from "@tabler/icons-react";

interface Props {
    account: IUserData;
    bio?: string;
    className?: string;
    style?: React.CSSProperties;
    theme?: IUserData["chosen_theme"];
}

export function UserImage({ account, className, style, theme }: Props) {
    return (
        <div
            className={cn("relative shrink-0", className)}
            id={"profile-picture"}
        >
            <img
                src={account.pic}
                className={cn("shrink-0", className)}
                style={
                    style
                        ? style
                        : {
                            width: "100%",
                            height: "100%",
                            WebkitMask:
                                masks[
                                theme?.heading.profile_picture.shape ||
                                "mask-circle"
                                ],
                            mask: masks[
                                theme?.heading.profile_picture.shape ||
                                "mask-circle"
                            ],
                        }
                }
                alt={account.username}
            />
        </div>
    );
}

export function HeadingTitle({ account, className, theme }: Props) {
    return (
        <div
            className={cn(
                "relative flex w-full items-center justify-center gap-2",
                className
            )}
        >
            <p
                className={cn("w-fit text-center text-xl font-bold")}
                style={{ color: theme?.profile.colors.heading_text }}
                id={"display-name"}
            >
                {account.displayname}
            </p>
            <div className="flex items-center">
                <ProfileBadges
                    user={account}
                    colour={theme?.profile.colors.heading_text}
                />
            </div>
        </div>
    );
}

const domainPattern =
    /([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?:\/([\w./-]+))?/g;

function renderBioWithLinks(bio: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    for (const match of bio.matchAll(domainPattern)) {
        const fullMatch = match[0];
        const domain = match[1];
        const path = match[2];
        const index = match.index ?? 0;

        if (index > lastIndex) {
            parts.push(bio.slice(lastIndex, index));
        }

        const href = `https://${domain}${path ? `/${path}` : ""}`;

        parts.push(
            <a
                key={`${index}-${fullMatch}`}
                className="underline"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {fullMatch}
            </a>,
        );

        lastIndex = index + fullMatch.length;
    }

    if (lastIndex < bio.length) {
        parts.push(bio.slice(lastIndex));
    }

    return parts;
}
export function HeadingBio({
    bio = "",
    className,
    theme,
}: Props): React.JSX.Element {
    return (
        <p
            id="user-bio"
            className={cn(
                "whitespace-pre-wrap text-center text-lg",
                className,
            )}
            style={{ color: theme?.profile?.colors?.heading_text }}
        >
            {renderBioWithLinks(bio)}
        </p>
    );
}
export function HeadingPronouns({ account, className, theme }: Props) {
    return (
        <p
            id="user-pronouns"
            className="flex items-center gap-1 text-lg"
            style={{ color: theme?.profile.colors.heading_text }}
        >
            <IconUser size={18} />
            {account.settings.pronouns}
        </p>
    );
}
export function HeadingLocation({ account, className, theme }: Props) {
    return (
        <p
            id="user-location"
            className="flex items-center gap-1 text-lg"
            style={{ color: theme?.profile.colors.heading_text }}
        >
            <IconLocation size={18} />
            {account.settings.location}
        </p>
    );
}
